import { EllipsisVertical } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { type ReactNode, useState } from "react";
import ConfirmDialog from "./ConfirmDialog.tsx";
import DropdownMenu from "./DropdownMenu.tsx";

export interface RowAction {
  label: string;
  icon?: ReactNode;
  /** The inline primary's tooltip when it should say more than the label (why it's disabled). */
  tooltip?: ReactNode;
  /** May return a promise: a confirmed action keeps its dialog spinning until it settles. */
  onClick: () => unknown;
  /** Red, sorted last, behind a divider. Always asks first: give it a `confirm`. */
  destructive?: boolean;
  disabled?: boolean;
  /** Leave the action out (e.g. the user may not do it). */
  hidden?: boolean;
  /** Ask before running. */
  confirm?: {
    title: string;
    body?: ReactNode;
    confirmLabel?: string;
    /** When "Cancel" would be ambiguous ("Cancel invitation" → "Keep"). */
    cancelLabel?: string;
    color?: "error" | "warning";
  };
}

interface RowActionsProps {
  /** Names the row in the kebab's aria-label: "More actions for {label}". */
  label: string;
  /**
   * The one frequent, non-destructive action that stays one click away. With
   * an icon it renders as an icon button with a tooltip; without, as a text button.
   */
  primary?: RowAction;
  actions?: RowAction[];
}

/**
 * A table row's actions: at most one inline, the rest in a kebab menu.
 * Destructive actions come last, in red, and confirm before they run.
 */
export default function RowActions({ label, primary, actions = [] }: RowActionsProps) {
  // Kept after closing so the dialog's text doesn't blank while it fades out.
  const [confirming, setConfirming] = useState<RowAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const run = (action: RowAction) => {
    if (action.confirm) {
      setConfirming(action);
      setConfirmOpen(true);
      return;
    }
    // A rejected mutateAsync has already toasted its error; don't leave it unhandled.
    void Promise.resolve(action.onClick()).catch(() => {});
  };

  const confirm = async () => {
    if (!confirming) return;
    setPending(true);
    try {
      await confirming.onClick();
      setConfirmOpen(false);
    } catch {
      // The mutation's own error toast says what went wrong; keep the dialog open.
    } finally {
      setPending(false);
    }
  };

  const visible = actions.filter((a) => !a.hidden);
  const safe = visible.filter((a) => !a.destructive);
  const destructive = visible.filter((a) => a.destructive);
  const showPrimary = primary && !primary.hidden;

  const item = (a: RowAction) => (
    <MenuItem
      key={a.label}
      disabled={a.disabled}
      onClick={() => run(a)}
      sx={a.destructive ? { color: "error.main" } : undefined}
    >
      {a.icon && <ListItemIcon sx={a.destructive ? { color: "inherit" } : undefined}>{a.icon}</ListItemIcon>}
      <ListItemText>{a.label}</ListItemText>
    </MenuItem>
  );

  return (
    <Box
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}
      onClick={(e) => e.stopPropagation()}
    >
      {showPrimary &&
        (primary.icon ? (
          <Tooltip title={primary.tooltip ?? primary.label}>
            <span>
              <IconButton
                size="small"
                aria-label={`${primary.label}: ${label}`}
                disabled={primary.disabled}
                onClick={() => run(primary)}
              >
                {primary.icon}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button size="small" disabled={primary.disabled} onClick={() => run(primary)}>
            {primary.label}
          </Button>
        ))}
      {visible.length > 0 && (
        <DropdownMenu
          aria-label={`More actions for ${label}`}
          icon={<EllipsisVertical fontSize="small" />}
          align="right"
          buttonProps={{ size: "small" }}
        >
          {safe.map(item)}
          {safe.length > 0 && destructive.length > 0 && <Divider />}
          {destructive.map(item)}
        </DropdownMenu>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={confirming?.confirm?.title ?? ""}
        confirmLabel={confirming?.confirm?.confirmLabel ?? confirming?.label}
        cancelLabel={confirming?.confirm?.cancelLabel}
        color={confirming?.confirm?.color ?? (confirming?.destructive ? "error" : "warning")}
        pending={pending}
        onConfirm={() => void confirm()}
        onClose={() => setConfirmOpen(false)}
      >
        {confirming?.confirm?.body}
      </ConfirmDialog>
    </Box>
  );
}
